/**
 * AdminSpecialTopicsPage - Manage Special Date Topics
 * 
 * Admin UI for creating and managing holiday/event topic overrides.
 * Special topics override the normal 28-day cycle for specific dates.
 * 
 * Updated Jan 2026: Added Bulk Import feature for preloading cultural calendars.
 */
import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import apiClient from "../../api/client";
import { AuthContext } from "../../App";
import { 
  Calendar, ArrowLeft, Plus, Trash2, Edit2, Check, X, 
  Search, Filter, AlertCircle, Sparkles, Upload, FileJson, 
  CheckCircle2, XCircle, Loader2
} from "lucide-react";
import AdminHelp from "../../components/admin/AdminHelp";

const API = window.location.origin;

function formatDate(dateStr) {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-");
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[parseInt(month) - 1]} ${parseInt(day)}, ${year}`;
}

function SpecialTopicCard({ topic, onEdit, onDelete }) {
  const isPast = new Date(topic.date) < new Date(new Date().toDateString());
  
  return (
    <div className={`rounded-xl border p-4 ${
      topic.active 
        ? isPast 
          ? "border-slate-700 bg-slate-900/50 opacity-60"
          : "border-purple-500/30 bg-purple-500/5"
        : "border-slate-800 bg-slate-900/30 opacity-50"
    }`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium text-slate-200">
              {formatDate(topic.date)}
            </span>
            {topic.title && (
              <span className="px-2 py-0.5 rounded-full text-xs bg-purple-500/20 text-purple-300">
                {topic.title}
              </span>
            )}
            {!topic.active && (
              <span className="px-2 py-0.5 rounded-full text-xs bg-slate-700 text-slate-400">
                Inactive
              </span>
            )}
            {isPast && topic.active && (
              <span className="px-2 py-0.5 rounded-full text-xs bg-slate-700 text-slate-400">
                Past
              </span>
            )}
          </div>
          <p className="text-sm text-slate-400 line-clamp-2">{topic.topic_text}</p>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onEdit(topic)}
            className="p-1.5 rounded-lg hover:bg-slate-700 transition text-slate-400 hover:text-slate-200"
            title="Edit"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(topic)}
            className="p-1.5 rounded-lg hover:bg-red-500/20 transition text-slate-400 hover:text-red-400"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function TopicModal({ topic, rooms, onSave, onClose }) {
  const isEditing = !!topic?.id;
  const [form, setForm] = useState({
    room_slug: topic?.room_slug || "",
    date: topic?.date || "",
    title: topic?.title || "",
    topic_text: topic?.topic_text || "",
    active: topic?.active ?? true,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.room_slug || !form.date || !form.topic_text) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSaving(true);
    try {
      await onSave(form, topic?.id);
      onClose();
    } catch (err) {
      // Error handled in onSave
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 rounded-2xl border border-slate-700 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-slate-800">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-100">
              {isEditing ? "Edit Special Topic" : "Add Special Topic"}
            </h2>
            <button onClick={onClose} className="p-1 hover:bg-slate-800 rounded-lg transition">
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Room Selector */}
          <div>
            <label className="block text-sm text-slate-400 mb-1">Room *</label>
            <select
              value={form.room_slug}
              onChange={(e) => setForm(f => ({ ...f, room_slug: e.target.value }))}
              className="w-full rounded-xl border border-slate-700 bg-slate-800 px-4 py-2.5 text-slate-100 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
              disabled={isEditing}
              data-testid="room-select"
            >
              <option value="">Select a room...</option>
              {rooms.map(r => (
                <option key={r.slug || r.room_key} value={r.slug || r.room_key}>
                  {r.name || r.slug || r.room_key}
                </option>
              ))}
            </select>
          </div>

          {/* Date Picker */}
          <div>
            <label className="block text-sm text-slate-400 mb-1">Date *</label>
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))}
              className="w-full rounded-xl border border-slate-700 bg-slate-800 px-4 py-2.5 text-slate-100 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
              disabled={isEditing}
              data-testid="date-input"
            />
          </div>

          {/* Title (Optional) */}
          <div>
            <label className="block text-sm text-slate-400 mb-1">
              Title <span className="text-slate-500">(optional)</span>
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="e.g., Valentine's Day, Juneteenth"
              maxLength={100}
              className="w-full rounded-xl border border-slate-700 bg-slate-800 px-4 py-2.5 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
              data-testid="title-input"
            />
          </div>

          {/* Topic Text */}
          <div>
            <label className="block text-sm text-slate-400 mb-1">Topic Text *</label>
            <textarea
              value={form.topic_text}
              onChange={(e) => setForm(f => ({ ...f, topic_text: e.target.value }))}
              placeholder="What's the conversation topic for this special day?"
              rows={4}
              minLength={10}
              maxLength={500}
              className="w-full rounded-xl border border-slate-700 bg-slate-800 px-4 py-2.5 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 resize-none"
              data-testid="topic-text-input"
            />
            <div className="text-xs text-slate-500 mt-1 text-right">
              {form.topic_text.length}/500
            </div>
          </div>

          {/* Active Toggle */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setForm(f => ({ ...f, active: !f.active }))}
              className={`w-12 h-6 rounded-full transition-colors ${
                form.active ? "bg-purple-500" : "bg-slate-700"
              }`}
              data-testid="active-toggle"
            >
              <div className={`w-5 h-5 rounded-full bg-white transform transition-transform ${
                form.active ? "translate-x-6" : "translate-x-0.5"
              }`} />
            </button>
            <span className="text-sm text-slate-300">
              {form.active ? "Active" : "Inactive"}
            </span>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-800">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl border border-slate-700 text-slate-300 hover:bg-slate-800 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-medium transition disabled:opacity-50"
              data-testid="save-topic-btn"
            >
              {saving ? "Saving..." : isEditing ? "Update" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * BulkImportModal - Upload JSON file to bulk import special topics
 */
function BulkImportModal({ rooms, onImportComplete, onClose, token }) {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  // Parse uploaded file
  const handleFileChange = useCallback((e) => {
    const selectedFile = e.target.files?.[0];
    setFile(selectedFile);
    setParsedData(null);
    setParseError(null);
    setResults(null);

    if (!selectedFile) return;

    // Only accept JSON
    if (!selectedFile.name.endsWith('.json')) {
      setParseError("Please upload a JSON file (.json)");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const json = JSON.parse(event.target.result);
        
        // Validate structure
        if (!Array.isArray(json)) {
          setParseError("JSON must be an array of topic objects");
          return;
        }

        // Validate each item has required fields
        const validItems = [];
        const errors = [];
        
        json.forEach((item, idx) => {
          if (!item.room_slug) {
            errors.push(`Row ${idx + 1}: missing room_slug`);
          } else if (!item.date) {
            errors.push(`Row ${idx + 1}: missing date`);
          } else if (!item.topic_text || item.topic_text.length < 10) {
            errors.push(`Row ${idx + 1}: topic_text must be at least 10 characters`);
          } else if (!/^\d{4}-\d{2}-\d{2}$/.test(item.date)) {
            errors.push(`Row ${idx + 1}: invalid date format (use YYYY-MM-DD)`);
          } else {
            validItems.push({
              room_slug: item.room_slug,
              date: item.date,
              title: item.title || null,
              topic_text: item.topic_text,
              timezone: item.timezone || "America/Chicago",
              active: item.active !== false
            });
          }
        });

        if (errors.length > 0 && validItems.length === 0) {
          setParseError(`Validation errors:\n${errors.slice(0, 5).join('\n')}${errors.length > 5 ? `\n... and ${errors.length - 5} more` : ''}`);
          return;
        }

        setParsedData({
          items: validItems,
          warnings: errors,
          totalRows: json.length
        });
      } catch (err) {
        setParseError(`Invalid JSON: ${err.message}`);
      }
    };
    reader.readAsText(selectedFile);
  }, []);

  // Execute bulk import
  const handleImport = async () => {
    if (!parsedData?.items?.length) return;

    setImporting(true);
    try {
      const res = await apiClient.post(`/admin/rooms/special-topics/bulk-import`,
        parsedData.items,
        { headers }
      );
      
      setResults(res.data);
      
      if (res.data.failed === 0) {
        toast.success(`Imported ${res.data.created} new, updated ${res.data.updated} existing topics`);
      } else {
        toast.warning(`Import completed with ${res.data.failed} failures`);
      }
      
      onImportComplete();
    } catch (err) {
      const msg = err.response?.data?.detail || "Import failed";
      toast.error(msg);
    } finally {
      setImporting(false);
    }
  };

  // Get room name helper
  const getRoomName = (slug) => {
    const room = rooms.find(r => (r.slug || r.room_key) === slug);
    return room?.name || slug;
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 rounded-2xl border border-slate-700 w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-5 border-b border-slate-800 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
              <Upload className="w-5 h-5 text-purple-400" />
              Bulk Import Special Topics
              <AdminHelp helpKey="bulk_import" size="sm" />
            </h2>
            <button onClick={onClose} className="p-1 hover:bg-slate-800 rounded-lg transition">
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
          <p className="text-sm text-slate-400 mt-1">
            Upload a JSON file to import multiple special topics at once
          </p>
        </div>

        {/* Content */}
        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          {/* File Upload */}
          {!results && (
            <div className="space-y-3">
              <label className="block">
                <div className="border-2 border-dashed border-slate-700 rounded-xl p-6 text-center hover:border-purple-500/50 transition cursor-pointer">
                  <FileJson className="w-10 h-10 text-slate-500 mx-auto mb-2" />
                  <div className="text-sm text-slate-300 mb-1">
                    {file ? file.name : "Click to select JSON file"}
                  </div>
                  <div className="text-xs text-slate-500">
                    or drag and drop
                  </div>
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleFileChange}
                    className="hidden"
                    data-testid="bulk-import-file-input"
                  />
                </div>
              </label>

              {/* Parse Error */}
              {parseError && (
                <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4">
                  <div className="flex items-start gap-2">
                    <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-red-200 whitespace-pre-wrap">{parseError}</div>
                  </div>
                </div>
              )}

              {/* Parsed Preview */}
              {parsedData && (
                <div className="space-y-3">
                  {/* Summary */}
                  <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-4">
                    <div className="text-sm text-slate-300 mb-2">
                      <span className="font-medium text-slate-100">{parsedData.items.length}</span> valid topics found
                      {parsedData.warnings.length > 0 && (
                        <span className="ml-2 text-amber-400">
                          ({parsedData.warnings.length} rows with issues)
                        </span>
                      )}
                    </div>
                    
                    {/* Warnings */}
                    {parsedData.warnings.length > 0 && (
                      <div className="text-xs text-amber-300/70 bg-amber-500/10 rounded-lg p-2 mt-2">
                        {parsedData.warnings.slice(0, 3).map((w, i) => (
                          <div key={i}>{w}</div>
                        ))}
                        {parsedData.warnings.length > 3 && (
                          <div>... and {parsedData.warnings.length - 3} more</div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Preview Table */}
                  <div className="text-sm text-slate-400 mb-1">Preview (first 5 rows):</div>
                  <div className="rounded-xl border border-slate-700 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-800">
                        <tr>
                          <th className="px-3 py-2 text-left text-slate-400 font-medium">Date</th>
                          <th className="px-3 py-2 text-left text-slate-400 font-medium">Room</th>
                          <th className="px-3 py-2 text-left text-slate-400 font-medium">Title</th>
                          <th className="px-3 py-2 text-left text-slate-400 font-medium">Topic</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800">
                        {parsedData.items.slice(0, 5).map((item, idx) => (
                          <tr key={idx} className="bg-slate-900/50">
                            <td className="px-3 py-2 text-slate-300">{item.date}</td>
                            <td className="px-3 py-2 text-slate-300">{getRoomName(item.room_slug)}</td>
                            <td className="px-3 py-2 text-purple-300">{item.title || "-"}</td>
                            <td className="px-3 py-2 text-slate-400 truncate max-w-[200px]">{item.topic_text}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {parsedData.items.length > 5 && (
                      <div className="px-3 py-2 text-xs text-slate-500 bg-slate-800/50">
                        ... and {parsedData.items.length - 5} more
                      </div>
                    )}
                  </div>

                  {/* Format Example */}
                  <details className="text-xs">
                    <summary className="text-slate-500 cursor-pointer hover:text-slate-400">
                      Expected JSON format
                    </summary>
                    <pre className="mt-2 p-3 rounded-lg bg-slate-800 text-slate-400 overflow-x-auto">
{`[
  {
    "room_slug": "her_space",
    "date": "2026-02-14",
    "title": "Valentine's Day",
    "topic_text": "What does love look like for you right now?",
    "timezone": "America/Chicago",
    "active": true
  }
]`}
                    </pre>
                  </details>
                </div>
              )}
            </div>
          )}

          {/* Import Results */}
          {results && (
            <div className="space-y-4">
              {/* Summary */}
              <div className={`rounded-xl border p-4 ${
                results.failed === 0 
                  ? "border-emerald-500/30 bg-emerald-500/10" 
                  : "border-amber-500/30 bg-amber-500/10"
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {results.failed === 0 ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-amber-400" />
                  )}
                  <span className="font-medium text-slate-100">Import Complete</span>
                </div>
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-slate-100">{results.total}</div>
                    <div className="text-xs text-slate-400">Total</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-emerald-400">{results.created}</div>
                    <div className="text-xs text-slate-400">Created</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-blue-400">{results.updated}</div>
                    <div className="text-xs text-slate-400">Updated</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-red-400">{results.failed}</div>
                    <div className="text-xs text-slate-400">Failed</div>
                  </div>
                </div>
              </div>

              {/* Failed Rows */}
              {results.failed > 0 && (
                <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4">
                  <div className="text-sm font-medium text-red-300 mb-2">Failed Rows:</div>
                  <div className="space-y-1 text-xs text-red-200/80 max-h-32 overflow-y-auto">
                    {results.results.filter(r => !r.success).map((r, i) => (
                      <div key={i}>
                        Row {r.row_index + 1} ({r.room_slug}, {r.date}): {r.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-slate-800 flex-shrink-0 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl border border-slate-700 text-slate-300 hover:bg-slate-800 transition"
          >
            {results ? "Close" : "Cancel"}
          </button>
          {!results && parsedData && (
            <button
              onClick={handleImport}
              disabled={importing || !parsedData.items.length}
              className="px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-medium transition disabled:opacity-50 flex items-center gap-2"
              data-testid="confirm-import-btn"
            >
              {importing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Import {parsedData.items.length} Topics
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminSpecialTopicsPage() {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [topics, setTopics] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTopic, setEditingTopic] = useState(null);
  const [showBulkImport, setShowBulkImport] = useState(false);
  
  // Filters
  const [selectedRoom, setSelectedRoom] = useState(searchParams.get("room") || "");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [showInactive, setShowInactive] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  async function fetchData() {
    try {
      setLoading(true);
      
      // Build query params
      const params = new URLSearchParams();
      if (selectedRoom) params.append("room", selectedRoom);
      if (selectedMonth) params.append("month", selectedMonth);
      if (showInactive) params.append("include_inactive", "true");
      
      const [topicsRes, roomsRes] = await Promise.all([
        apiClient.get(`/admin/rooms/special-topics?${params}`, { headers }),
        apiClient.get(`/admin/content/rooms`, { headers }),
      ]);
      
      setTopics(topicsRes.data);
      setRooms(roomsRes.data.filter(r => !r.is_legacy_slug && !r.is_hidden));
    } catch (err) {
      console.error("[SpecialTopics] Failed to load:", err);
      toast.error("Failed to load special topics");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
  }, [token, selectedRoom, selectedMonth, showInactive]);

  async function handleSave(form, existingId) {
    try {
      if (existingId) {
        // Update existing
        await apiClient.put(`/admin/rooms/special-topics/${existingId}`, form, { headers });
        toast.success("Special topic updated");
      } else {
        // Create new (use upsert to handle duplicates gracefully)
        await apiClient.post(`/admin/rooms/special-topics/upsert`, form, { headers });
        toast.success("Special topic created");
      }
      await fetchData();
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to save";
      toast.error(msg);
      throw err;
    }
  }

  async function handleDelete(topic) {
    if (!confirm(`Delete special topic for ${formatDate(topic.date)}?`)) return;
    
    try {
      await apiClient.delete(`/admin/rooms/special-topics/${topic.id}`, { headers });
      toast.success("Special topic deleted");
      await fetchData();
    } catch (err) {
      toast.error("Failed to delete");
    }
  }

  function handleEdit(topic) {
    setEditingTopic(topic);
    setShowModal(true);
  }

  function handleAdd() {
    setEditingTopic(null);
    setShowModal(true);
  }

  // Group topics by room
  const topicsByRoom = useMemo(() => {
    const grouped = {};
    for (const topic of topics) {
      const key = topic.room_slug;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(topic);
    }
    // Sort each room's topics by date
    for (const key of Object.keys(grouped)) {
      grouped[key].sort((a, b) => a.date.localeCompare(b.date));
    }
    return grouped;
  }, [topics]);

  // Get upcoming topics (next 30 days)
  const upcomingTopics = useMemo(() => {
    const today = new Date();
    const thirtyDaysFromNow = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
    return topics.filter(t => {
      const d = new Date(t.date);
      return t.active && d >= today && d <= thirtyDaysFromNow;
    }).sort((a, b) => a.date.localeCompare(b.date));
  }, [topics]);

  if (loading && topics.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 text-slate-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 text-slate-100">
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <header className="border-b border-slate-800 pb-4">
          <button
            onClick={() => navigate("/admin")}
            className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200 transition mb-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Admin Dashboard
          </button>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <Sparkles className="w-6 h-6 text-purple-400" />
                  Special Date Topics
                </h1>
                <AdminHelp helpKey="special_date_topics" />
              </div>
              <p className="text-sm text-slate-400 mt-1">
                Override daily topics for holidays and special events
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowBulkImport(true)}
                className="px-4 py-2 rounded-xl border border-purple-500/50 text-purple-300 hover:bg-purple-500/10 font-medium transition flex items-center gap-2"
                data-testid="bulk-import-btn"
              >
                <Upload className="w-4 h-4" />
                Bulk Import
              </button>
              <button
                onClick={handleAdd}
                className="px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-medium transition flex items-center gap-2"
                data-testid="add-topic-btn"
              >
                <Plus className="w-4 h-4" />
                Add Topic
              </button>
            </div>
          </div>
        </header>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-500" />
            <select
              value={selectedRoom}
              onChange={(e) => setSelectedRoom(e.target.value)}
              className="rounded-xl border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
              data-testid="filter-room"
            >
              <option value="">All Rooms</option>
              {rooms.map(r => (
                <option key={r.slug || r.room_key} value={r.slug || r.room_key}>
                  {r.name || r.slug}
                </option>
              ))}
            </select>
          </div>
          
          <input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="rounded-xl border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
            placeholder="Filter by month"
            data-testid="filter-month"
          />

          <label className="flex items-center gap-2 text-sm text-slate-400">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded border-slate-600"
            />
            Show inactive
          </label>
        </div>

        {/* Upcoming Topics Alert */}
        {upcomingTopics.length > 0 && (
          <div className="rounded-xl border border-purple-500/30 bg-purple-500/10 p-4">
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-medium text-purple-200">Upcoming Special Topics</div>
                <div className="text-sm text-purple-200/80 mt-1 space-y-1">
                  {upcomingTopics.slice(0, 3).map(t => (
                    <div key={t.id}>
                      <span className="font-medium">{formatDate(t.date)}</span>
                      {t.title && <span className="ml-2 text-purple-300">({t.title})</span>}
                      <span className="ml-2 text-slate-400">· {t.room_slug}</span>
                    </div>
                  ))}
                  {upcomingTopics.length > 3 && (
                    <div className="text-purple-300">+{upcomingTopics.length - 3} more</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Topics List */}
        {Object.keys(topicsByRoom).length === 0 ? (
          <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-12 text-center">
            <AlertCircle className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <div className="text-slate-400">No special topics found</div>
            <p className="text-sm text-slate-500 mt-1">
              Add special topics for holidays like Valentine's Day, Juneteenth, or Pride month
            </p>
            <button
              onClick={handleAdd}
              className="mt-4 px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 transition"
            >
              Create your first special topic
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(topicsByRoom).map(([roomSlug, roomTopics]) => {
              const room = rooms.find(r => (r.slug || r.room_key) === roomSlug);
              return (
                <div key={roomSlug} className="rounded-2xl border border-slate-800 bg-slate-900/50 p-4">
                  <h3 className="text-lg font-medium text-slate-200 mb-3 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    {room?.name || roomSlug}
                    <span className="text-xs text-slate-500 font-normal">
                      ({roomTopics.length} topics)
                    </span>
                  </h3>
                  <div className="space-y-2">
                    {roomTopics.map(topic => (
                      <SpecialTopicCard
                        key={topic.id}
                        topic={topic}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Topic Modal */}
      {showModal && (
        <TopicModal
          topic={editingTopic}
          rooms={rooms}
          onSave={handleSave}
          onClose={() => {
            setShowModal(false);
            setEditingTopic(null);
          }}
        />
      )}

      {/* Bulk Import Modal */}
      {showBulkImport && (
        <BulkImportModal
          rooms={rooms}
          token={token}
          onImportComplete={() => fetchData()}
          onClose={() => setShowBulkImport(false)}
        />
      )}
    </div>
  );
}
